/* ==========================================================================
   VITKONWOOD Product Tabs
   Based on the client's export. Desktop tab bar and mobile accordion share
   the same panels via data-target, so switching one syncs the other.

   Changes from the original export:
   - Re-initialises on Shopify section load/select events so the section works
     inside the theme editor.
   - Guards against double-binding if the script is loaded more than once.
   - Arrow key navigation on the desktop tablist (expected for role="tablist").
   ========================================================================== */
(function () {
  var INITIALISED = 'vitkonTabsReady';

  function activate(container, targetId) {
    if (!targetId) return;

    container.querySelectorAll('.vitkon-product-tabs__tab').forEach(function (t) {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });
    container.querySelectorAll('.vitkon-accordion-header').forEach(function (h) {
      h.classList.remove('is-active');
      h.setAttribute('aria-expanded', 'false');
    });
    container.querySelectorAll('.vitkon-product-tabs__panel').forEach(function (p) {
      p.classList.remove('is-active');
    });

    var panel = container.querySelector('#' + targetId);
    if (panel) panel.classList.add('is-active');

    var tab = container.querySelector('.vitkon-product-tabs__tab[data-target="' + targetId + '"]');
    if (tab) {
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');
    }

    var header = container.querySelector('.vitkon-accordion-header[data-target="' + targetId + '"]');
    if (header) {
      header.classList.add('is-active');
      header.setAttribute('aria-expanded', 'true');
    }

    return tab;
  }

  /* Scroll the tab bar itself so the active tab is visible when the bar
     overflows. Done by setting scrollLeft rather than scrollIntoView, because
     scrollIntoView also moves the page vertically and fights with
     scrollTabsToTop below. */
  function centreTabInBar(container, tab) {
    var bar = container.querySelector('.vitkon-product-tabs__nav-container');
    if (!bar || bar.scrollWidth <= bar.clientWidth) return;

    var target = tab.offsetLeft - (bar.clientWidth - tab.offsetWidth) / 2;
    target = Math.max(0, Math.min(target, bar.scrollWidth - bar.clientWidth));

    if (typeof bar.scrollTo === 'function') {
      bar.scrollTo({ left: target, behavior: 'smooth' });
    } else {
      bar.scrollLeft = target;
    }
  }

  /* Switching tab keeps the page scroll position, so a visitor who has read
     part of a long tab lands half way down the next one. Bring them back to
     the top of the tab content, allowing for the sticky header and the sticky
     tab bar sitting under it.

     Only scrolls UP. If the section is still below the fold the visitor is
     already above the content, and yanking the page down would be worse than
     doing nothing. */
  function scrollTabsToTop(container) {
    /* Measure from the CONTAINER, never from the nav bar.

       The bar is position:sticky, so once it is stuck its
       getBoundingClientRect().top is always exactly the sticky offset. Using it
       made target === current on every call, so the guard below always fired
       and the page never moved. The container does not stick, so its position
       is a true reference. */
    var offset = parseInt(container.style.getPropertyValue('--vitkon-sticky-offset'), 10) || 0;
    var current = window.pageYOffset || document.documentElement.scrollTop;
    var target = Math.round(container.getBoundingClientRect().top + current - offset);

    if (current <= target + 1) return;

    if ('scrollBehavior' in document.documentElement.style) {
      window.scrollTo({ top: target, behavior: 'smooth' });
    } else {
      window.scrollTo(0, target);
    }
  }

  /* Mobile equivalent of scrollTabsToTop. Opening an accordion closes the
     previous one, so the page height changes and the visitor can end up
     looking at unrelated content. Bring the header they tapped to the top.

     Unlike the desktop version this scrolls in BOTH directions: closing a long
     panel above can pull the tapped header upwards, so "only scroll up" would
     leave it off screen.

     The measurement runs in requestAnimationFrame, after the browser has
     applied the open/close class changes and reflowed. Measuring first would
     use the pre-reflow position and land in the wrong place. */
  function scrollHeaderToTop(container, header) {
    if (!header) return;

    window.requestAnimationFrame(function () {
      var offset = parseInt(container.style.getPropertyValue('--vitkon-sticky-offset'), 10) || 0;
      var current = window.pageYOffset || document.documentElement.scrollTop;
      var target = Math.round(header.getBoundingClientRect().top + current - offset);

      target = Math.max(0, target);
      if (Math.abs(target - current) < 2) return;

      if ('scrollBehavior' in document.documentElement.style) {
        window.scrollTo({ top: target, behavior: 'smooth' });
      } else {
        window.scrollTo(0, target);
      }
    });
  }

  var FAQ_MS = 260;

  function prefersReducedMotion() {
    return window.matchMedia &&
           window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clearFaqInline(el) {
    el.style.transition = '';
    el.style.height = '';
    el.style.opacity = '';
    el.style.overflow = '';
    el.style.paddingTop = '';
    el.style.paddingBottom = '';
  }

  /* Open or close one FAQ answer with a height transition.

     Opening:  set details.open first so the answer has layout to measure,
               then animate 0 -> measured height.
     Closing:  animate current height -> 0, and only set details.open = false
               after, otherwise the browser hides the content instantly and
               there is nothing left to animate.

     A timer rather than transitionend, because transitionend does not fire
     reliably if the element is hidden mid-animation (e.g. the visitor switches
     tab while an answer is closing). */
  function faqSetOpen(details, shouldOpen) {
    var content = details.querySelector('.vitkon-faq-answer');

    if (!content || prefersReducedMotion()) {
      details.open = shouldOpen;
      return;
    }

    if (details.getAttribute('data-vitkon-animating') === 'true') return;
    details.setAttribute('data-vitkon-animating', 'true');

    var styles = window.getComputedStyle(content);
    var padTop = styles.paddingTop;
    var padBottom = styles.paddingBottom;

    if (shouldOpen) {
      details.open = true;
      var openHeight = content.scrollHeight;

      content.style.overflow = 'hidden';
      content.style.height = '0px';
      content.style.opacity = '0';
      content.style.paddingTop = '0px';
      content.style.paddingBottom = '0px';
      void content.offsetHeight; // force the browser to apply the start state

      content.style.transition =
        'height ' + FAQ_MS + 'ms ease, opacity ' + FAQ_MS + 'ms ease, padding ' + FAQ_MS + 'ms ease';
      content.style.height = openHeight + 'px';
      content.style.opacity = '1';
      content.style.paddingTop = padTop;
      content.style.paddingBottom = padBottom;

      window.setTimeout(function () {
        clearFaqInline(content);
        details.setAttribute('data-vitkon-animating', 'false');
      }, FAQ_MS);

    } else {
      /* details.open stays true until the animation ends, but the chevron
         rotation is keyed off [open] in CSS. Without this class the arrow
         would only flip back after the content had already collapsed. */
      details.classList.add('is-closing');

      content.style.overflow = 'hidden';
      content.style.height = content.scrollHeight + 'px';
      content.style.opacity = '1';
      void content.offsetHeight;

      content.style.transition =
        'height ' + FAQ_MS + 'ms ease, opacity ' + FAQ_MS + 'ms ease, padding ' + FAQ_MS + 'ms ease';
      content.style.height = '0px';
      content.style.opacity = '0';
      content.style.paddingTop = '0px';
      content.style.paddingBottom = '0px';

      window.setTimeout(function () {
        details.open = false;
        details.classList.remove('is-closing');
        clearFaqInline(content);
        details.setAttribute('data-vitkon-animating', 'false');
      }, FAQ_MS);
    }
  }

  function initContainer(container) {
    if (container.dataset[INITIALISED] === 'true') return;
    container.dataset[INITIALISED] = 'true';

    var headers = container.querySelectorAll('.vitkon-accordion-header');
    var panels = container.querySelectorAll('.vitkon-product-tabs__panel');
    var tabs = container.querySelectorAll('.vitkon-product-tabs__tab');

    // Ensure something is open on load
    var hasActivePanel = false;
    panels.forEach(function (p) {
      if (p.classList.contains('is-active')) hasActivePanel = true;
    });

    if (!hasActivePanel && headers.length > 0) {
      headers[0].classList.add('is-active');
      headers[0].setAttribute('aria-expanded', 'true');
      if (panels[0]) panels[0].classList.add('is-active');
      if (tabs[0]) {
        tabs[0].classList.add('is-active');
        tabs[0].setAttribute('aria-selected', 'true');
      }
    }

    // 1. Desktop tabs
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        var activated = activate(container, tab.getAttribute('data-target'));

        if (activated) {
          centreTabInBar(container, activated);
          scrollTabsToTop(container);
        }
      });

      // Arrow key navigation, expected behaviour for role="tablist"
      tab.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();

        var list = Array.prototype.slice.call(container.querySelectorAll('.vitkon-product-tabs__tab'));
        var index = list.indexOf(tab);
        if (index === -1) return;

        var nextIndex = e.key === 'ArrowRight'
          ? (index + 1) % list.length
          : (index - 1 + list.length) % list.length;

        var next = list[nextIndex];
        activate(container, next.getAttribute('data-target'));

        // preventScroll so the browser's own focus scrolling does not fight
        // with centreTabInBar / scrollTabsToTop
        try {
          next.focus({ preventScroll: true });
        } catch (err) {
          next.focus();
        }

        centreTabInBar(container, next);
        scrollTabsToTop(container);
      });
    });

    // 2. Mobile accordion
    headers.forEach(function (header) {
      header.addEventListener('click', function (e) {
        e.preventDefault();
        var targetId = header.getAttribute('data-target');
        var panel = container.querySelector('#' + targetId);

        if (header.classList.contains('is-active')) {
          // Collapse the open one. No scroll: the visitor is already looking
          // at this header and moving the page under them would be jarring.
          header.classList.remove('is-active');
          header.setAttribute('aria-expanded', 'false');
          if (panel) panel.classList.remove('is-active');
        } else {
          activate(container, targetId);
          scrollHeaderToTop(container, header);
        }
      });
    });

    /* 3. FAQ: one answer open at a time, animated.

       A native <details> snaps open and shut, because its height jumps between
       0 and auto with nothing to transition. So the summary click is
       intercepted, the answer's height is animated, and details.open is only
       flipped once the close animation has finished.

       Exclusivity is handled here too rather than with the native
       <details name="..."> accordion, which older browsers ignore. */
    var faqItems = Array.prototype.slice.call(
      container.querySelectorAll('.vitkon-faq-item')
    );

    faqItems.forEach(function (item) {
      var summary = item.querySelector('.vitkon-faq-question');
      if (!summary) return;

      summary.addEventListener('click', function (e) {
        e.preventDefault();

        if (item.open) {
          faqSetOpen(item, false);
          return;
        }

        // Close whatever is open, then open this one. Both animate.
        faqItems.forEach(function (other) {
          if (other !== item && other.open) {
            faqSetOpen(other, false);
          }
        });
        faqSetOpen(item, true);
      });
    });
  }

  /* The desktop tab bar is position:sticky. The theme's header is also sticky
     but exposes no height variable, so measure it and hand the value to CSS as
     --vitkon-sticky-offset. Without this the tab bar would slide underneath
     the header instead of stopping below it. */
  function setStickyOffset() {
    var containers = document.querySelectorAll('.vitkon-product-tabs');
    if (!containers.length) return;

    var header = document.querySelector('.header-sticky-wrapper .site-header') ||
                 document.querySelector('.site-header');
    var offset = 0;

    if (header) {
      var styles = window.getComputedStyle(header);
      // Only offset for a header that actually stays on screen
      if (styles.position === 'fixed' || styles.position === 'sticky' ||
          header.closest('[data-sticky="true"]') ||
          document.querySelector('.header-sticky-wrapper')) {
        offset = Math.round(header.getBoundingClientRect().height);
      }
    }

    containers.forEach(function (c) {
      c.style.setProperty('--vitkon-sticky-offset', offset + 'px');
    });
  }

  var resizeTimer;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(setStickyOffset, 150);
  }

  function initVitkonProductTabs(root) {
    var scope = root || document;
    scope.querySelectorAll('.vitkon-product-tabs').forEach(initContainer);
    setStickyOffset();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initVitkonProductTabs();
    });
  } else {
    initVitkonProductTabs();
  }

  // Header height changes with the viewport, so re-measure on resize
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  // Theme editor support
  document.addEventListener('shopify:section:load', function (e) {
    initVitkonProductTabs(e.target);
  });

  document.addEventListener('shopify:section:select', function (e) {
    initVitkonProductTabs(e.target);
  });
})();
